import type { SignInOptions } from '@myrewards/contracts';
import type { QueryClient } from '@tanstack/react-query';
import { useEffect, useState, type ReactElement } from 'react';
import { useLoaderData, useSearchParams } from 'react-router';
import {
  FALLBACK_SIGN_IN_OPTIONS,
  safeReturnTo,
  serverSignInPath,
  signInOptionsQuery,
} from '../api';
import { logos, photoWallColumns, signInVideo } from '../assets';
import { prefersReducedMotion, useReducedMotion } from '../design/motion';
import { leaveTo } from '../session/leave';
import { Button, cx, Icon } from '../ui';
import { signInMessage, type SignInMessage } from './messages';
import { SetupSandboxNote } from '../shell/SetupSandboxBanner';
import s from './SignInPage.module.css';

/** Which splash to show. If the options can't be read, the photo wall (the seeded default). */
export async function signInLoader(queryClient: QueryClient): Promise<SignInOptions> {
  try {
    return await queryClient.query({ ...signInOptionsQuery, staleTime: 'static' });
  } catch {
    return FALLBACK_SIGN_IN_OPTIONS;
  }
}

const VALUES = ['Creativity', 'Integrity', 'Passion', 'Responsiveness', 'Collaboration'] as const;

/** How long the hand-off screen shows before the browser leaves for Microsoft Entra ID. */
const HAND_OFF_MS = 450;

/** Sign-in page (G0): splash A (video) or B (photo wall), then the hand-off to Entra ID. */
export function SignInPage(): ReactElement {
  const options = useLoaderData<typeof signInLoader>();
  const [params] = useSearchParams();
  const returnTo = safeReturnTo(params.get('returnTo'));
  const message = signInMessage(params);
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    if (!leaving) return;
    // Let the hand-off screen paint, then leave for Microsoft Entra ID (a full-page navigation).
    const timer = window.setTimeout(
      () => {
        leaveTo(serverSignInPath(returnTo));
      },
      prefersReducedMotion() ? 0 : HAND_OFF_MS,
    );
    return () => {
      window.clearTimeout(timer);
    };
  }, [leaving, returnTo]);

  // Coming back from Entra ID with the browser's Back button restores this page from the
  // back/forward cache, hand-off screen and all: show the splash again.
  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent): void => {
      if (event.persisted) setLeaving(false);
    };
    window.addEventListener('pageshow', onPageShow);
    return () => {
      window.removeEventListener('pageshow', onPageShow);
    };
  }, []);

  const signIn = (
    <>
      <SetupSandboxNote />
      <Button
        variant="gold"
        size="lg"
        className={s.signIn}
        icon={<Icon name="signIn" size={18} />}
        isDisabled={leaving}
        onPress={() => {
          setLeaving(true);
        }}
        data-testid="sign-in-button"
      >
        Sign in with your Microsoft Entra ID
      </Button>
    </>
  );

  return (
    <div className={s.page} data-splash={options.splash} data-leaving={leaving}>
      <title>Sign in · Areté MyRewards</title>
      {options.splash === 'video' ? (
        <SplashVideo message={message} signIn={signIn} />
      ) : (
        <SplashPhotoWall message={message} signIn={signIn} />
      )}
      {leaving && <HandOff />}
    </div>
  );
}

interface SplashProps {
  message: SignInMessage | null;
  signIn: ReactElement;
}

/** Splash B: the brand panel on the left, a slowly scrolling wall of photos on the right. */
function SplashPhotoWall({ message, signIn }: SplashProps): ReactElement {
  return (
    <>
      <main className={s.panel}>
        <img className={s.logo} src={logos.horizontalWhite} alt="Areté" width={150} height={40} />
        <div className={s.panelCopy}>
          <h1 className={s.wordmark}>
            My<span className={s.wordmarkGold}>Rewards</span>
          </h1>
          <p className={cx('disp', s.tagline)}>Behind every mission, people worth celebrating.</p>
          <ValuesTicker />
          {message && <Message message={message} />}
          <div className={s.action}>{signIn}</div>
        </div>
        <p className={cx('eyebrow', s.motto)}>Discover. Develop. Deliver.</p>
      </main>
      <div className={s.divider} aria-hidden="true" />
      <PhotoWall />
    </>
  );
}

/** Splash A: full-bleed video (the poster frame when motion is reduced), copy on the left. */
function SplashVideo({ message, signIn }: SplashProps): ReactElement {
  const reduced = useReducedMotion();
  return (
    <>
      <div className={s.videoLayer} aria-hidden="true">
        <img className={s.videoMedia} src={signInVideo.poster} alt="" />
        {!reduced && (
          <video
            className={s.videoMedia}
            src={signInVideo.src}
            poster={signInVideo.poster}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
          />
        )}
        <div className={s.videoWash} />
        <div className={s.videoFoot} />
      </div>
      <main className={s.videoContent}>
        <img className={s.logo} src={logos.horizontalWhite} alt="Areté" width={150} height={40} />
        <div className={s.videoCopy}>
          <h1 className={cx(s.wordmark, s.wordmarkLarge)}>
            My<span className={s.wordmarkCyan}>Rewards</span>
          </h1>
          <p className={cx('disp', s.headline)}>
            <span className={s.mask}>
              <span className={s.line}>Excellence,</span>
            </span>{' '}
            <span className={s.mask}>
              <span className={cx(s.line, s.lineGold)}>recognized.</span>
            </span>
          </p>
          <p className={s.videoSub}>
            Nominate, approve and celebrate the people behind Areté’s work — from seafloor to space.
          </p>
          {message && <Message message={message} />}
          <div className={s.action}>{signIn}</div>
        </div>
        <p className={cx('eyebrow', s.motto)}>Discover. Develop. Deliver.</p>
      </main>
    </>
  );
}

/** "Recognized for" and the five Areté values, one at a time (all five for screen readers). */
function ValuesTicker(): ReactElement {
  return (
    <div className={s.values}>
      <span className={cx('eyebrow', s.valuesLabel)}>Recognized for</span>
      <span className="visually-hidden">
        Creativity, Integrity, Passion, Responsiveness and Collaboration
      </span>
      <span className={cx('disp', s.valuesWords)} aria-hidden="true">
        {VALUES.map((value, index) => (
          <span key={value} className={cx(s.valueWord, index === 0 && s.valueFirst)}>
            {value}
          </span>
        ))}
      </span>
    </div>
  );
}

const WALL_TOASTS = [
  {
    icon: 'medal',
    tone: s.toastGold,
    place: s.wallToast1,
    eyebrow: 'Team Achievement Award',
    text: 'Congratulations to the sea trials crew — seven people, one flawless week at sea.',
  },
  {
    icon: 'star',
    tone: s.toastCyan,
    place: s.wallToast2,
    eyebrow: 'Sector Discretionary Bonus',
    text: '“Rebuilt the deck rig overnight so we made the flight window.” You’ve been recognized.',
  },
  {
    icon: 'trophy',
    tone: s.toastNavy,
    place: s.wallToast3,
    eyebrow: 'Professional Achievement Award',
    text: 'Approved by the Board — your letter is on its way. Congratulations!',
  },
] as const;

/** Decorative: three columns drifting up and down, with a few congratulation cards in turn. */
function PhotoWall(): ReactElement {
  return (
    <div className={s.wall} aria-hidden="true">
      {photoWallColumns.map((column, index) => (
        <div key={index} className={s.wallColumn}>
          <div className={cx(s.wallTrack, index === 1 && s.wallReverse, index === 2 && s.wallSlow)}>
            {[...column, ...column].map((src, i) => (
              <img key={`${src}-${i}`} src={src} alt="" className={s.wallPhoto} decoding="async" />
            ))}
          </div>
        </div>
      ))}
      <div className={s.wallTint} />
      <div className={s.wallFadeTop} />
      <div className={s.wallFadeBottom} />
      {WALL_TOASTS.map((toast) => (
        <div key={toast.eyebrow} className={cx(s.wallToast, toast.place)}>
          <span className={cx(s.wallToastIcon, toast.tone)}>
            <Icon name={toast.icon} size={20} />
          </span>
          <span className={s.wallToastText}>
            <span className={cx('eyebrow', s.wallToastEyebrow)}>{toast.eyebrow}</span>
            <span className={s.wallToastBody}>{toast.text}</span>
          </span>
        </div>
      ))}
    </div>
  );
}

function Message({ message }: { message: SignInMessage }): ReactElement {
  return (
    <section
      className={cx(s.message, message.tone === 'problem' && s.messageProblem)}
      aria-labelledby="sign-in-message"
    >
      <span className={s.messageIcon} aria-hidden="true">
        <Icon name={message.tone === 'problem' ? 'info' : 'check'} size={18} strokeWidth={2.2} />
      </span>
      <span className={s.messageText}>
        <h2 id="sign-in-message" className={s.messageTitle}>
          {message.title}
        </h2>
        <p className={s.messageBody}>{message.body}</p>
      </span>
    </section>
  );
}

/** "Taking you to Microsoft Entra ID…" while the browser leaves. */
function HandOff(): ReactElement {
  return (
    <div className={s.handOff} role="status">
      <div className={s.handOffCard}>
        <img src={logos.horizontalWhite} alt="Areté" width={127} height={34} />
        <span className={s.spinner} aria-hidden="true" />
        <span className={s.handOffTitle}>Taking you to Microsoft Entra ID…</span>
        <span className={s.handOffBody}>
          Sign in with your Areté work account. You’ll come straight back to MyRewards.
        </span>
      </div>
    </div>
  );
}
